import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ListMusic, Plus, Users, Clock, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { toast } from 'sonner';
import { useModalHistory } from '../hooks/useModalHistory';

const lineupsAPI = {
  getAll: () => api.get('/lineups'),
  create: (name) => api.post('/lineups', { name }),
  delete: (id) => api.delete(`/lineups/${id}`),
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const LineupCard = ({ lineup, currentUserId, onDelete }) => {
  const navigate = useNavigate();
  const isOwner = lineup.created_by === currentUserId;
  const sharedCount = (lineup.shared_with || []).length;

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(lineup.lineup_id, lineup.name);
  };

  return (
    <div
      data-testid={`lineup-card-${lineup.lineup_id}`}
      onClick={() => navigate(`/lineups/${lineup.lineup_id}`)}
      className="group relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 cursor-pointer hover:border-green-300 dark:hover:border-green-600 hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-green-50 dark:bg-green-500/10 flex items-center justify-center mt-0.5">
            <ListMusic className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 dark:text-white break-words leading-snug" data-testid={`lineup-name-${lineup.lineup_id}`}>
              {lineup.name}
            </p>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {!isOwner && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  by {lineup.owner_name}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                <ListMusic className="w-3 h-3" />
                {(lineup.items || []).length} song{(lineup.items || []).length !== 1 ? 's' : ''}
              </span>
              {sharedCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                  <Users className="w-3 h-3" />
                  {sharedCount} shared
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                <Clock className="w-3 h-3" />
                {formatDate(lineup.updated_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isOwner && (
            <button
              data-testid={`lineup-delete-${lineup.lineup_id}`}
              onClick={handleDelete}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-400 transition-colors" />
        </div>
      </div>
      {!isOwner && (
        <span className="absolute top-3 right-10 text-[10px] font-semibold uppercase tracking-wide text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-1.5 py-0.5 rounded">
          Shared
        </span>
      )}
    </div>
  );
};

export default function Lineups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lineups, setLineups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  // ── Back-button closes dialogs ───────────────────────────────────────────
  const createModal = useModalHistory(createOpen, setCreateOpen);

  // deleteConfirm is null|{id,name}, so we manage its history manually
  const deleteHistoryPushedRef = useRef(false);
  const deleteSuppressRef = useRef(false);
  const deleteConfirmRef = useRef(null);
  useEffect(() => { deleteConfirmRef.current = deleteConfirm; }, [deleteConfirm]);
  useEffect(() => {
    const handler = () => {
      if (deleteSuppressRef.current) { deleteSuppressRef.current = false; return; }
      if (deleteConfirmRef.current) { setDeleteConfirm(null); deleteHistoryPushedRef.current = false; }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const openDeleteDialog = (id, name) => {
    setDeleteConfirm({ id, name });
    window.history.pushState({ modal: true }, '');
    deleteHistoryPushedRef.current = true;
  };
  const closeDeleteDialog = () => {
    setDeleteConfirm(null);
    if (deleteHistoryPushedRef.current) {
      deleteSuppressRef.current = true;
      deleteHistoryPushedRef.current = false;
      window.history.back();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lineupsAPI.getAll();
      setLineups(res.data.lineups || []);
    } catch (err) {
      toast.error('Failed to load lineups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await lineupsAPI.create(newName.trim());
      setCreateOpen(false);
      setNewName('');
      navigate(`/lineups/${res.data.lineup_id}`);
    } catch {
      toast.error('Failed to create lineup');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await lineupsAPI.delete(deleteConfirm.id);
      setLineups(prev => prev.filter(l => l.lineup_id !== deleteConfirm.id));
      closeDeleteDialog();
      toast.success('Lineup deleted');
    } catch {
      toast.error('Failed to delete lineup');
    } finally {
      setDeleting(false);
    }
  };

  const myLineups = lineups.filter(l => l.created_by === user?.user_id);
  const sharedLineups = lineups.filter(l => l.created_by !== user?.user_id);

  return (
    <div className="bg-slate-50 dark:bg-slate-950">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lineups</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">Personal setlists you can share and promote</p>
          </div>
          <Button
            data-testid="create-lineup-btn"
            onClick={() => { createModal.open(); setNewName(''); }}
            className="shrink-0 bg-green-700 hover:bg-green-800 text-white rounded-lg gap-1.5 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Lineup
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : lineups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="lineups-empty">
            <div className="w-16 h-16 rounded-2xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center mb-4">
              <ListMusic className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-1">No lineups yet</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 max-w-xs">
              Create a personal setlist to plan your songs, then promote it to a service when ready.
            </p>
            <Button
              data-testid="create-lineup-empty-btn"
              onClick={() => { createModal.open(); setNewName(''); }}
              className="bg-green-700 hover:bg-green-800 text-white rounded-lg gap-2"
            >
              <Plus className="w-4 h-4" />
              Create your first lineup
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {myLineups.length > 0 && (
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">My Lineups</p>
                <div className="space-y-2">
                  {myLineups.map(lineup => (
                    <LineupCard
                      key={lineup.lineup_id}
                      lineup={lineup}
                      currentUserId={user?.user_id}
                      onDelete={(id, name) => openDeleteDialog(id, name)}
                    />
                  ))}
                </div>
              </section>
            )}
            {sharedLineups.length > 0 && (
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Shared With Me</p>
                <div className="space-y-2">
                  {sharedLineups.map(lineup => (
                    <LineupCard
                      key={lineup.lineup_id}
                      lineup={lineup}
                      currentUserId={user?.user_id}
                      onDelete={(id, name) => openDeleteDialog(id, name)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={createModal.onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="create-lineup-dialog">
          <DialogHeader>
            <DialogTitle>New Lineup</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Lineup name</label>
            <Input
              data-testid="lineup-name-input"
              placeholder="e.g. Sunday Morning Set"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => createModal.close()}>Cancel</Button>
            <Button
              data-testid="lineup-create-confirm-btn"
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="bg-green-700 hover:bg-green-800 text-white"
            >
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-lineup-dialog">
          <DialogHeader>
            <DialogTitle>Delete Lineup</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400 py-2">
            Are you sure you want to delete <span className="font-semibold text-slate-900 dark:text-white">"{deleteConfirm?.name}"</span>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialog}>Cancel</Button>
            <Button
              data-testid="lineup-delete-confirm-btn"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
